import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import AddStepButton from 'procedures/shared-components/step-config/step-nav/AddStepButton';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Accordion from 'ui-library/Accordion';
import {GRAY_LIGHT, GRAY_DARK} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

export interface ProcedureStepGroupProps {
  name: string;
  infoPopoverText?: string | null;
  isOpenByDefault?: boolean;
  showLockedIcon: boolean;
  children: React.ReactNode;
  onAddStepClicked?: (e: ElementMouseEvent) => void;
  onAddStepCompleted?: (stepIndex: number) => Promise<void>;
  isAddStepDisabled?: boolean;
  addStepTooltip?: string;
}

function StepGroupIcon({
  showLockedIcon,
  onAddStepClicked,
  isAddStepDisabled,
  addStepTooltip,
}: {
  showLockedIcon: boolean;
  onAddStepClicked?: (e: ElementMouseEvent) => void;
  isAddStepDisabled?: boolean;
  addStepTooltip?: string;
}): JSX.Element | null {
  if (showLockedIcon) {
    return (
      <div {...getTestProps({component: 'ProcedureStepGroup', element: 'icon'})}>
        <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />
      </div>
    );
  } else if (onAddStepClicked) {
    return (
      <AddStepButton
        tooltip={addStepTooltip}
        onAddButtonClicked={onAddStepClicked}
        disabled={isAddStepDisabled}
      />
    );
  }
  return null;
}

export default function ProcedureStepGroup({
  name,
  infoPopoverText,
  isOpenByDefault = true,
  showLockedIcon,
  children,
  onAddStepClicked,
  isAddStepDisabled,
  addStepTooltip,
}: ProcedureStepGroupProps): JSX.Element {
  return (
    <div
      className={classNames(css(styles.headerContainer), 'multi-window-editing-container')}
      {...getTestProps({
        component: 'ProcedureStepGroup',
        element: 'root',
        key: name,
      })}
      data-amp-safe={false}
    >
      <Accordion
        isOpenByDefault={isOpenByDefault}
        expandIconPosition="right"
        headerSpacing="none"
        headerStyle="bordered"
        header={
          <div className={css(styles.nameContainer)}>
            <span
              {...getTestProps({component: 'ProcedureStepGroup', element: 'name'})}
              className={css(styles.name)}
            >
              {name}
              {infoPopoverText != null && (
                <Popover
                  placement={FloatingPlacement.RIGHT}
                  interaction={FloatingInteraction.HOVER}
                  renderContent={() => <div>{infoPopoverText}</div>}
                >
                  <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
                </Popover>
              )}
              <StepGroupIcon
                showLockedIcon={showLockedIcon}
                onAddStepClicked={onAddStepClicked}
                isAddStepDisabled={isAddStepDisabled}
                addStepTooltip={addStepTooltip}
              />
            </span>
          </div>
        }
      >
        {children}
      </Accordion>
    </div>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRightWidth: 0,
    ':first-child': {
      borderTopWidth: 0,
    },
  },
  nameContainer: {
    paddingTop: '6px',
    paddingBottom: '6px',
    height: '32px',
    cursor: 'pointer',
    width: '215px',
  },
  name: {
    fontSize: '13px',
    fontWeight: 'bold',
    ...multiLineEllipsisText(1),
    paddingLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoIcon: {
    color: GRAY_DARK,
  },
  lockIcon: {
    color: GRAY_DARK,
    width: '12px',
  },
});
